const NONCE_LENGTH = 12;

export async function generateNonce(): Promise<Uint8Array> {
  const nonce = new Uint8Array(NONCE_LENGTH);
  crypto.getRandomValues(nonce);
  return nonce;
}

export async function generateAESKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  ) as Promise<CryptoKey>;
}

export async function encryptAES(
  key: CryptoKey,
  plaintext: ArrayBuffer
): Promise<{ ciphertext: ArrayBuffer; nonce: Uint8Array }> {
  const nonce = await generateNonce();
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce as unknown as BufferSource },
    key,
    plaintext
  );
  return { ciphertext, nonce };
}

export async function decryptAES(
  key: CryptoKey,
  ciphertext: ArrayBuffer,
  nonce: Uint8Array
): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonce as unknown as BufferSource },
    key,
    ciphertext
  );
}

export function concatNonceAndCiphertext(nonce: Uint8Array, ciphertext: ArrayBuffer): ArrayBuffer {
  const result = new Uint8Array(nonce.length + ciphertext.byteLength);
  result.set(nonce, 0);
  result.set(new Uint8Array(ciphertext), nonce.length);
  return result.buffer as ArrayBuffer;
}

export function splitNonceAndCiphertext(data: ArrayBuffer): { nonce: Uint8Array; ciphertext: ArrayBuffer } {
  const arr = new Uint8Array(data);
  const nonce = arr.slice(0, NONCE_LENGTH);
  const ciphertext = arr.slice(NONCE_LENGTH);
  return { nonce, ciphertext: ciphertext.buffer as ArrayBuffer };
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i];
    if (byte !== undefined) {
      binary += String.fromCharCode(byte);
    }
  }
  return btoa(binary);
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer as ArrayBuffer;
}